/* eslint-disable */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { signOut } from "firebase/auth";
import {
  ShieldCheck,
  LogOut,
  LockKeyhole,
  KeyRound,
  ArrowLeft,
  Timer,
} from "lucide-react";

import { auth, functions } from "../../firebase";
import { useAuth } from "../../contexts/AuthContext";
import { createAdminSession } from "../../utils/createAdminSession";
function extractLockMinutes(err) {
  const message = String(err?.message || "");
  const match = message.match(/(\d+)\s*minute/i);
  return match ? Number(match[1]) : 10;
}

function formatCountdown(seconds) {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
}

export default function AdminMFAChallenge() {
  const navigate = useNavigate();
  const { profile, markMfaSessionVerified } = useAuth();

  const inputRef = useRef(null);
  const recoveryInputRef = useRef(null);

  const [mode, setMode] = useState("totp");
  const [token, setToken] = useState("");
  const [recoveryCode, setRecoveryCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [lockedUntil, setLockedUntil] = useState(null);
  const [lockSecondsLeft, setLockSecondsLeft] = useState(0);

  const isLocked = lockSecondsLeft > 0;

  const tokenArray = useMemo(
    () => Array.from({ length: 6 }).map((_, i) => token[i] || ""),
    [token]
  );

  useEffect(() => {
    if (!lockedUntil) {
      setLockSecondsLeft(0);
      return;
    }

    const tick = () => {
      const diff = Math.max(0, Math.ceil((lockedUntil - Date.now()) / 1000));
      setLockSecondsLeft(diff);

      if (diff <= 0) {
        setLockedUntil(null);
        setError("");
      }
    };

    tick();

    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [lockedUntil]);

  function handleLockError(err) {
    const code = err?.code || "";
    const message = String(err?.message || "");

    if (code.includes("resource-exhausted") || message.includes("Too many")) {
      const mins = extractLockMinutes(err);
      const until = Date.now() + mins * 60 * 1000;

      setLockedUntil(until);
      setToken("");
      setRecoveryCode("");
      setError(`Too many failed attempts. Try again in ${mins} minute(s).`);
      return true;
    }

    return false;
  }

  async function verifyTotp() {
    if (isLocked) return;

    try {
      setLoading(true);
      setError("");

      const verifyAdminMfa = httpsCallable(functions, "verifyAdminMfa");
      await verifyAdminMfa({ token });

      markMfaSessionVerified();
      await createAdminSession({
  uid: auth.currentUser?.uid,
  schoolId: profile?.schoolId,
  email: auth.currentUser?.email,
  displayName: profile?.displayName,
  mfaVerified: true,
});

navigate("/admin-manager", { replace: true });
    } catch (err) {
      console.error(err);

      if (handleLockError(err)) return;

      setError(
        "Invalid authentication code. Please use the latest code from your authenticator app."
      );

      setTimeout(() => {
        setToken("");
        inputRef.current?.focus();
      }, 120);
    } finally {
      setLoading(false);
    }
  }

  async function verifyRecovery() {
    if (isLocked) return;

    try {
      setLoading(true);
      setError("");

      const verifyRecoveryCode = httpsCallable(functions, "verifyRecoveryCode");
      await verifyRecoveryCode({ code: recoveryCode.trim() });

      markMfaSessionVerified();
      navigate("/admin-manager", { replace: true });
    } catch (err) {
      console.error(err);

      if (handleLockError(err)) return;

      setError("Invalid or already-used recovery code.");

      setTimeout(() => {
        setRecoveryCode("");
        recoveryInputRef.current?.focus();
      }, 120);
    } finally {
      setLoading(false);
    }
  }

  async function logoutNow() {
    await signOut(auth);
    navigate("/login", { replace: true });
  }

  return (
    <div style={styles.page}>
      <div style={styles.glowOne} />
      <div style={styles.glowTwo} />

      <div style={styles.card}>
        <div style={styles.hero}>
          <div style={styles.heroCircle}>
            {isLocked ? (
              <Timer size={42} color="#ffffff" />
            ) : mode === "totp" ? (
              <ShieldCheck size={42} color="#ffffff" />
            ) : (
              <KeyRound size={42} color="#ffffff" />
            )}
          </div>
          <div style={styles.ringOne} />
          <div style={styles.ringTwo} />
        </div>

        <div style={styles.eyebrow}>
          {isLocked ? (
            <Timer size={14} />
          ) : mode === "totp" ? (
            <ShieldCheck size={14} />
          ) : (
            <KeyRound size={14} />
          )}
          {isLocked
            ? "TEMPORARILY LOCKED"
            : mode === "totp"
            ? "ADMIN VERIFICATION"
            : "RECOVERY ACCESS"}
        </div>

        <h1 style={styles.title}>
          {isLocked
            ? "Security lock active"
            : mode === "totp"
            ? "Enter your authenticator code"
            : "Use a recovery code"}
        </h1>

        <p style={styles.text}>
          {isLocked
            ? "Too many failed attempts were detected. Please wait before trying again."
            : mode === "totp"
            ? "For your security, enter the latest 6-digit code from your authenticator app to continue."
            : "Enter one of your saved one-time recovery codes to access your admin account."}
        </p>

        <div style={styles.userCard}>
          <div style={styles.userIcon}>
            <LockKeyhole size={18} />
          </div>

          <div style={{ flex: 1 }}>
            <div style={styles.userName}>
              {profile?.displayName || "Administrator"}
            </div>
            <div style={styles.userEmail}>{profile?.email}</div>
          </div>

          <div style={styles.adminBadge}>Admin</div>
        </div>

        {isLocked ? (
          <>
            <div style={styles.lockCard}>
              <div style={styles.lockIcon}>
                <Timer size={22} />
              </div>

              <div style={{ flex: 1 }}>
                <div style={styles.lockTitle}>MFA temporarily locked</div>
                <div style={styles.lockText}>
                  Try again in{" "}
                  <strong>{formatCountdown(lockSecondsLeft)}</strong>. This
                  protects admin accounts against brute-force attempts.
                </div>
              </div>
            </div>

            {error ? <div style={styles.error}>{error}</div> : null}

            <button disabled style={{ ...styles.primary, opacity: 0.55 }}>
              <Timer size={18} />
              Locked — {formatCountdown(lockSecondsLeft)}
            </button>
          </>
        ) : mode === "totp" ? (
          <>
            <div style={styles.codeLabel}>6-DIGIT CODE</div>

            <div
              style={styles.codeBoxes}
              onClick={() => inputRef.current?.focus()}
            >
              {tokenArray.map((digit, index) => (
                <div
                  key={index}
                  style={{
                    ...styles.codeBox,
                    borderColor:
                      index === token.length ? "#5b4dff" : "#dbe7ff",
                    boxShadow:
                      index === token.length
                        ? "0 0 0 4px rgba(91,77,255,0.08)"
                        : "none",
                  }}
                >
                  {digit || "•"}
                </div>
              ))}
            </div>

            <input
              ref={inputRef}
              id="mfa-code"
              name="mfa-code"
              autoComplete="one-time-code"
              value={token}
              onChange={(e) => {
                setToken(e.target.value.replace(/\D/g, "").slice(0, 6));
                if (error) setError("");
              }}
              inputMode="numeric"
              autoFocus
              maxLength={6}
              style={styles.hiddenInput}
            />

            <div style={styles.helpCard}>
              <div style={styles.helpIcon}>
                <ShieldCheck size={18} />
              </div>

              <div>
                <div style={styles.helpTitle}>Can't access your code?</div>
                <div style={styles.helpText}>
                  Use a saved recovery code if your authenticator device is
                  unavailable.
                </div>
              </div>
            </div>

            {error ? <div style={styles.error}>{error}</div> : null}

            <button
              onClick={verifyTotp}
              disabled={loading || token.length !== 6}
              style={{
                ...styles.primary,
                opacity: loading || token.length !== 6 ? 0.6 : 1,
              }}
            >
              <ShieldCheck size={18} />
              {loading ? "Verifying..." : "Verify and continue"}
            </button>

            <button
              type="button"
              onClick={() => {
                setMode("recovery");
                setError("");
                setTimeout(() => recoveryInputRef.current?.focus(), 80);
              }}
              style={styles.linkButton}
            >
              <KeyRound size={17} />
              Use recovery code instead
            </button>
          </>
        ) : (
          <>
            <div style={styles.codeLabel}>RECOVERY CODE</div>

            <input
              ref={recoveryInputRef}
              id="mfa-recovery-code"
              name="mfa-recovery-code"
              autoComplete="one-time-code"
              value={recoveryCode}
              onChange={(e) => {
                setRecoveryCode(e.target.value.toUpperCase().trim());
                if (error) setError("");
              }}
              placeholder="XXXXXXXX-XXXXXXXX"
              style={styles.recoveryInput}
            />

            <div style={styles.helpCard}>
              <div style={styles.helpIcon}>
                <KeyRound size={18} />
              </div>

              <div>
                <div style={styles.helpTitle}>One-time use only</div>
                <div style={styles.helpText}>
                  Once accepted, this recovery code is permanently marked as
                  used.
                </div>
              </div>
            </div>

            {error ? <div style={styles.error}>{error}</div> : null}

            <button
              onClick={verifyRecovery}
              disabled={loading || recoveryCode.length < 8}
              style={{
                ...styles.primary,
                opacity: loading || recoveryCode.length < 8 ? 0.6 : 1,
              }}
            >
              <KeyRound size={18} />
              {loading ? "Checking..." : "Use recovery code"}
            </button>

            <button
              type="button"
              onClick={() => {
                setMode("totp");
                setError("");
                setTimeout(() => inputRef.current?.focus(), 80);
              }}
              style={styles.linkButton}
            >
              <ArrowLeft size={17} />
              Back to authenticator code
            </button>
          </>
        )}

        <button onClick={logoutNow} style={styles.secondary}>
          <LogOut size={18} />
          Logout
        </button>

        <div style={styles.footer}>
          Secure admin access protected by server-side multi-factor
          authentication
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "32px 20px",
    background: "linear-gradient(180deg,#eef2ff 0%,#f8fafc 100%)",
    position: "relative",
    overflow: "hidden",
  },

  glowOne: {
    position: "absolute",
    width: 420,
    height: 420,
    borderRadius: "50%",
    background: "radial-gradient(circle,#7c3aed22 0%,transparent 70%)",
    top: -180,
    left: -180,
  },

  glowTwo: {
    position: "absolute",
    width: 340,
    height: 340,
    borderRadius: "50%",
    background: "radial-gradient(circle,#5b4dff15 0%,transparent 70%)",
    bottom: -120,
    right: -120,
  },

  card: {
    width: "100%",
    maxWidth: 520,
    background: "rgba(255,255,255,0.88)",
    backdropFilter: "blur(18px)",
    WebkitBackdropFilter: "blur(18px)",
    border: "1px solid rgba(219,231,255,0.9)",
    borderRadius: 24,
    padding: 24,
    position: "relative",
    boxShadow: "0 25px 70px rgba(15,23,42,0.08)",
  },

  hero: {
    position: "relative",
    width: 68,
    height: 68,
    margin: "0 auto 20px",
  },

  heroCircle: {
    width: 68,
    height: 68,
    borderRadius: "50%",
    background: "linear-gradient(135deg,#5b4dff,#7c3aed)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    zIndex: 2,
    boxShadow: "0 20px 40px rgba(91,77,255,0.28)",
  },

  ringOne: {
    position: "absolute",
    inset: -10,
    borderRadius: "50%",
    border: "2px solid rgba(91,77,255,0.10)",
  },

  ringTwo: {
    position: "absolute",
    inset: -20,
    borderRadius: "50%",
    border: "2px solid rgba(91,77,255,0.06)",
  },

  eyebrow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    fontSize: 11,
    fontWeight: 900,
    letterSpacing: "0.18em",
    color: "#5b4dff",
    marginBottom: 14,
  },

  title: {
    fontSize: 30,
    lineHeight: 1.05,
    textAlign: "center",
    fontWeight: 950,
    color: "#102043",
    margin: 0,
    marginBottom: 16,
  },

  text: {
    textAlign: "center",
    color: "#64748b",
    fontSize: 14,
    lineHeight: 1.6,
    maxWidth: 480,
    margin: "0 auto 28px",
  },

  userCard: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    background: "linear-gradient(180deg,#ffffff,#f8fbff)",
    border: "1px solid #dbe7ff",
    borderRadius: 20,
    padding: 14,
    marginBottom: 20,
  },

  userIcon: {
    width: 48,
    height: 48,
    borderRadius: 16,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg,#eef2ff,#f5f3ff)",
    color: "#5b4dff",
  },

  userName: {
    fontWeight: 900,
    fontSize: 18,
    color: "#102043",
    marginBottom: 4,
  },

  userEmail: {
    color: "#64748b",
    fontSize: 14,
  },

  adminBadge: {
    background: "linear-gradient(135deg,#ede9fe,#f5f3ff)",
    color: "#6d28d9",
    fontWeight: 900,
    padding: "8px 14px",
    borderRadius: 999,
    fontSize: 12,
  },

  codeLabel: {
    fontSize: 13,
    fontWeight: 900,
    letterSpacing: "0.16em",
    color: "#64748b",
    marginBottom: 14,
  },

  codeBoxes: {
    display: "grid",
    gridTemplateColumns: "repeat(6,1fr)",
    gap: 10,
    marginBottom: 22,
    cursor: "text",
  },

  codeBox: {
    height: 54,
    borderRadius: 14,
    border: "2px solid #dbe7ff",
    background: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 20,
    fontWeight: 900,
    color: "#102043",
    transition: "all .2s ease",
  },

  hiddenInput: {
    position: "absolute",
    width: 1,
    height: 1,
    opacity: 0,
    left: -9999,
  },

  recoveryInput: {
    width: "100%",
    height: 58,
    borderRadius: 18,
    border: "2px solid #dbe7ff",
    background: "#ffffff",
    color: "#102043",
    fontSize: 18,
    fontWeight: 900,
    letterSpacing: "0.08em",
    padding: "0 18px",
    outline: "none",
    boxSizing: "border-box",
    marginBottom: 22,
    textAlign: "center",
  },

  helpCard: {
    display: "flex",
    gap: 14,
    alignItems: "flex-start",
    padding: 18,
    borderRadius: 20,
    background: "linear-gradient(180deg,#ffffff,#f8fbff)",
    border: "1px solid #e2e8f0",
    marginBottom: 20,
  },

  helpIcon: {
    width: 40,
    height: 40,
    borderRadius: 12,
    background: "linear-gradient(135deg,#eef2ff,#f5f3ff)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#5b4dff",
  },

  helpTitle: {
    fontSize: 16,
    fontWeight: 900,
    color: "#102043",
    marginBottom: 4,
  },

  helpText: {
    color: "#64748b",
    lineHeight: 1.5,
    fontSize: 14,
  },

  lockCard: {
    display: "flex",
    gap: 14,
    alignItems: "flex-start",
    padding: 18,
    borderRadius: 20,
    background: "#fff7ed",
    border: "1px solid #fed7aa",
    color: "#9a3412",
    marginBottom: 20,
  },

  lockIcon: {
    width: 42,
    height: 42,
    borderRadius: 14,
    background: "#ffedd5",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#c2410c",
  },

  lockTitle: {
    fontSize: 16,
    fontWeight: 950,
    marginBottom: 4,
  },

  lockText: {
    fontSize: 14,
    lineHeight: 1.5,
  },

  error: {
    background: "#fef2f2",
    color: "#dc2626",
    padding: 14,
    borderRadius: 16,
    marginBottom: 16,
    fontWeight: 800,
    border: "1px solid #fecaca",
    fontSize: 14,
  },

  primary: {
    width: "100%",
    height: 50,
    border: 0,
    borderRadius: 14,
    background: "linear-gradient(135deg,#5b4dff,#7c3aed)",
    color: "#fff",
    fontWeight: 900,
    fontSize: 14,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    boxShadow: "0 16px 34px rgba(91,77,255,0.24)",
    transition: "all .2s ease",
  },

  linkButton: {
    width: "100%",
    height: 52,
    border: "none",
    background: "transparent",
    color: "#5b4dff",
    fontWeight: 900,
    fontSize: 15,
    cursor: "pointer",
    marginTop: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
  },

  secondary: {
    width: "100%",
    height: 50,
    borderRadius: 14,
    border: "1px solid #dbe7ff",
    background: "#fff",
    color: "#102043",
    fontWeight: 900,
    fontSize: 14,
    cursor: "pointer",
    marginTop: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
  },

  footer: {
    textAlign: "center",
    marginTop: 22,
    color: "#64748b",
    fontSize: 13,
    fontWeight: 700,
  },
};